import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, Card, Field, Select, Btn, DataTable, FilterBar, txnColumns, ReceiptDialog, StatusBadge } from "@/components/shared";

export const REPORTS = {
  recharge: { title: "Recharge Report", services: ["Mobile Recharge", "DTH"] },
  bbps: { title: "Bill Payment Report", services: ["BBPS", "Credit Card Bill"] },
  dmt: { title: "DMT Report", services: ["DMT", "Account Verification"] },
  aeps: { title: "AEPS Report", services: ["AEPS Cash Withdrawal", "AEPS Balance Enquiry", "AEPS Mini Statement", "Aadhaar Pay", "Cash Deposit"] },
  settlement: { title: "Settlement / Payout Report", services: ["Payout", "Wallet Transfer"] },
  pg: { title: "Payment Gateway & QR Report", services: ["Payment Gateway", "QR Collect", "PAN Verification"] },
  all: { title: "All Services Report", services: [] },
  "pending-dmt": { title: "Pending DMT", services: ["DMT", "Payout"], status: "PENDING" },
};

const REASONS = ["Amount debited but not credited", "Wrong number / account", "Transaction pending too long", "Refund not received", "Other"];

function ComplaintDialog({ txn, onClose }) {
  const [f, setF] = useState({ reason: REASONS[0], remarks: "" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/transactions/${txn.txn_id}/complaint`, f);
      toast.success(`Complaint raised: ${data.ticket_no}`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="complaint-dialog">
        <DialogHeader><DialogTitle className="font-display">Raise complaint · {txn?.txn_id}</DialogTitle></DialogHeader>
        <Field label="Reason"><Select options={REASONS} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="complaint-reason-select" /></Field>
        <Field label="Remarks"><textarea className="np-input h-20 py-2" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="complaint-remarks-input" /></Field>
        <Btn loading={loading} onClick={submit} data-testid="complaint-submit-button">Submit Complaint</Btn>
      </DialogContent>
    </Dialog>
  );
}

export function ServiceReport({ type }) {
  const cfg = REPORTS[type];
  const [rows, setRows] = useState([]);
  const [service, setService] = useState("");
  const [status, setStatus] = useState(cfg.status || "");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [comp, setComp] = useState(null);

  const load = async (f = {}) => {
    setLoading(true);
    const svc = service || cfg.services.join(",") || undefined;
    const { data } = await api.get("/transactions", { params: { ...f, service: svc, status: status || undefined, q: q || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { setService(""); setStatus(cfg.status || ""); }, [type]); // eslint-disable-line
  useEffect(() => { load(); }, [type]); // eslint-disable-line

  const checkStatus = async (r) => {
    try { const { data } = await api.post(`/payout/${r.txn_id}/status`); toast.info(`Status: ${data.status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [...txnColumns(setRec), { label: "Bank Status", render: (r) => r.service === "Payout" && r.status === "PENDING" ? <button onClick={() => checkStatus(r)} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`check-status-${r.txn_id}`}>Check</button> : "-" }, { label: "Complaint", render: (r) => <button onClick={() => setComp(r)} className="text-xs font-semibold text-amber-700 hover:underline" data-testid={`complaint-btn-${r.txn_id}`}>Raise</button> }];
  const ok = rows.filter((r) => r.status === "SUCCESS");
  return (
    <div>
      <PageHeader title={cfg.title} subtitle={`${rows.length} transactions`} />
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <Card><div className="np-label">Success Amount</div><div className="font-mono text-lg font-semibold" data-testid="report-success-amount">{inr(ok.reduce((s, r) => s + r.amount, 0))}</div></Card>
        <Card><div className="np-label">Success Count</div><div className="text-lg font-semibold">{ok.length}</div></Card>
        <Card><div className="np-label">Commission</div><div className="font-mono text-lg font-semibold text-emerald-700">{inr(rows.reduce((s, r) => s + r.commission, 0))}</div></Card>
        <Card><div className="np-label">Charges</div><div className="font-mono text-lg font-semibold text-red-600">{inr(rows.reduce((s, r) => s + r.surcharge, 0))}</div></Card>
      </div>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName={type + "-report"} exportCols={cols.slice(0, 10)}>
        {cfg.services.length > 1 && <Field label="Service"><Select options={cfg.services} placeholder="All" value={service} onChange={(e) => setService(e.target.value)} data-testid="report-service-filter" /></Field>}
        <Field label="Status"><Select options={["SUCCESS", "PENDING", "FAILED", "REFUNDED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="report-status-filter" /></Field>
        <Field label="Txn ID / Number"><input className="np-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="report-search-input" /></Field>
      </FilterBar>
      <DataTable columns={cols} rows={rows} testid="report-table" />
      <ReceiptDialog txn={rec} onClose={() => setRec(null)} />
      <ComplaintDialog txn={comp} onClose={() => setComp(null)} />
    </div>
  );
}

export function Statement({ url = "/ledger", title = "Statement / Ledger", subtitle = "Every credit and debit on your wallets with opening & closing balance." }) {
  const [rows, setRows] = useState([]);
  const [wallet, setWallet] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get(url, { params: { ...f, wallet: wallet || undefined, type: type || undefined } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [url]); // eslint-disable-line
  const cols = [
    { label: "Date", render: (r) => fmtDate(r.created_at), csv: (r) => r.created_at },
    { label: "Ref", key: "ref", render: (r) => <span className="font-mono text-xs">{r.ref}</span> },
    { label: "Wallet", key: "wallet", render: (r) => <span className="uppercase text-xs font-bold">{r.wallet}</span> },
    { label: "Service", key: "service" },
    { label: "Description", key: "description" },
    { label: "Opening", key: "opening", render: (r) => <span className="font-mono">{inr(r.opening)}</span> },
    { label: "Credit", render: (r) => r.type === "CR" ? <span className="font-mono text-emerald-700">{inr(r.amount)}</span> : "", csv: (r) => (r.type === "CR" ? r.amount : "") },
    { label: "Debit", render: (r) => r.type === "DR" ? <span className="font-mono text-red-600">{inr(r.amount)}</span> : "", csv: (r) => (r.type === "DR" ? r.amount : "") },
    { label: "Closing", key: "closing", render: (r) => <span className="font-mono font-semibold">{inr(r.closing)}</span> },
  ];
  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="statement" exportCols={cols}>
        <Field label="Wallet"><Select options={[{ value: "main", label: "Main" }, { value: "aeps", label: "AEPS" }]} placeholder="All" value={wallet} onChange={(e) => setWallet(e.target.value)} data-testid="statement-wallet-filter" /></Field>
        <Field label="Type"><Select options={[{ value: "CR", label: "Credit" }, { value: "DR", label: "Debit" }]} placeholder="All" value={type} onChange={(e) => setType(e.target.value)} data-testid="statement-type-filter" /></Field>
      </FilterBar>
      <DataTable columns={cols} rows={rows} testid="statement-table" />
    </div>
  );
}

export function Commission() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/commission/summary", { params: f });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const cols = [
    { label: "Service", key: "service" }, { label: "Txns", key: "count" },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono">{inr(r.amount)}</span> },
    { label: "Commission", key: "commission", render: (r) => <span className="font-mono text-emerald-700">{inr(r.commission)}</span> },
    { label: "TDS", key: "tds", render: (r) => <span className="font-mono">{inr(r.tds)}</span> },
    { label: "Net Earning", render: (r) => <span className="font-mono font-semibold">{inr(r.commission - r.tds)}</span>, csv: (r) => r.commission - r.tds },
    { label: "Charges Paid", key: "surcharge", render: (r) => <span className="font-mono text-red-600">{inr(r.surcharge)}</span> },
  ];
  const tot = rows.reduce((s, r) => s + r.commission - r.tds, 0);
  return (
    <div>
      <PageHeader title="Commission & Earnings"><div className="rounded-lg bg-emerald-600 px-4 py-2 text-white"><div className="text-[10px] uppercase tracking-widest opacity-80">Net earnings</div><div className="font-mono text-xl font-semibold" data-testid="commission-total">{inr(tot)}</div></div></PageHeader>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="commission" exportCols={cols} />
      <DataTable columns={cols} rows={rows} testid="commission-table" />
    </div>
  );
}

export function Slabs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/slabs").then(({ data }) => setRows(data)); }, []);
  return (
    <div>
      <PageHeader title="Commission Slab List" subtitle="Current commission & charges applicable to your retailer account." />
      <DataTable testid="slab-table" rows={rows} columns={[
        { label: "Service", key: "service" }, { label: "Operator", key: "operator" }, { label: "Amount Range", key: "range" },
        { label: "Type", key: "type" }, { label: "Value", render: (r) => <span className="font-mono font-semibold">{r.value}</span> },
      ]} />
    </div>
  );
}

export { StatusBadge };

const PAYOUT_SRC = {
  retailer: { url: "/transactions", title: "Move to Bank Report" },
  dt: { url: "/dt/transactions", title: "Move to Bank Report — My Network" },
  admin: { url: "/admin/transactions", title: "Move to Bank Report — All Users" },
};

export function PayoutReport({ scope = "retailer" }) {
  const src = PAYOUT_SRC[scope];
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const load = async (f = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get(src.url, { params: { ...f, service: "Payout", status: status || undefined } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope]); // eslint-disable-line
  const check = async (r) => {
    try { const { data } = await api.post(`/payout/${r.txn_id}/status`); toast.info(`Status: ${data.status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const d = (r) => r.details || {};
  const cols = [
    { label: "Date", render: (r) => <span className="text-slate-500">{fmtDate(r.created_at)}</span>, csv: (r) => r.created_at },
    { label: "Txn ID", key: "txn_id", render: (r) => <span className="font-mono text-xs">{r.txn_id}</span> },
    ...(scope !== "retailer" ? [{ label: "User", render: (r) => `${r.retailer_name} (${r.login_id})`, csv: (r) => `${r.retailer_name} (${r.login_id})` }] : []),
    { label: "Account Holder", render: (r) => d(r).holder_name, csv: (r) => d(r).holder_name },
    { label: "Bank", key: "operator" },
    { label: "Account No.", key: "number", render: (r) => <span className="font-mono text-xs">{r.number}</span> },
    { label: "IFSC", render: (r) => <span className="font-mono text-xs">{d(r).ifsc}</span>, csv: (r) => d(r).ifsc },
    { label: "Mode", render: (r) => d(r).mode, csv: (r) => d(r).mode },
    { label: "Type", render: (r) => d(r).transfer_type || "Saved Bank", csv: (r) => d(r).transfer_type || "Saved Bank" },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono font-semibold">{inr(r.amount)}</span> },
    { label: "Charge", key: "surcharge", render: (r) => <span className="font-mono text-red-600">{inr(r.surcharge)}</span> },
    { label: "Status", key: "status", render: (r) => <StatusBadge status={r.status} /> },
    { label: "UTR", render: (r) => <span className="font-mono text-xs" data-testid={`payout-utr-${r.txn_id}`}>{d(r).utr || "-"}</span>, csv: (r) => d(r).utr || "" },
    { label: "Remarks", render: (r) => <span className="text-xs text-slate-500">{d(r).provider_response || d(r).reason || "-"}</span>, csv: (r) => d(r).provider_response || d(r).reason || "" },
    { label: "Action", render: (r) => (
      <div className="flex gap-3 text-xs font-semibold">
        {r.status === "PENDING" && scope !== "dt" && <button onClick={() => check(r)} className="text-blue-700 hover:underline" data-testid={`payout-check-${r.txn_id}`}>Check</button>}
        <button onClick={() => setRec(r)} className="text-slate-700 hover:underline" data-testid={`payout-receipt-${r.txn_id}`}>Receipt</button>
      </div>) },
  ];
  const sum = (s) => rows.filter((r) => r.status === s).reduce((a, r) => a + r.amount, 0);
  const tiles = [["Success", sum("SUCCESS"), "text-emerald-700"], ["Pending", sum("PENDING"), "text-amber-600"], ["Failed / Refunded", sum("FAILED") + sum("REFUNDED"), "text-red-600"], ["Charges", rows.reduce((a, r) => a + r.surcharge, 0), "text-slate-800"]];
  return (
    <div>
      <PageHeader title={src.title} subtitle={`${rows.length} bank transfers with UTR and bank status`} />
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        {tiles.map(([l, v, c]) => <Card key={l}><div className="np-label">{l}</div><div className={`font-mono text-lg font-semibold ${c}`} data-testid={`payout-total-${l.toLowerCase().replace(/\W+/g, "-")}`}>{inr(v)}</div></Card>)}
      </div>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="move-to-bank-report" exportCols={cols.slice(0, -1)}>
        <Field label="Status"><Select options={["SUCCESS", "PENDING", "FAILED", "REFUNDED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payout-report-status-filter" /></Field>
      </FilterBar>
      <DataTable columns={cols} rows={rows} testid="payout-report-table" />
      <ReceiptDialog txn={rec} onClose={() => setRec(null)} />
    </div>
  );
}
